// Client → server trace for outbound-frame ATTEMPTS.
//
// The structural blind spot this closes: the server can only log frames it
// RECEIVED. When a user pressed Apply and nothing happened, "the client never
// sent it" and "the frame was lost in flight" produce byte-identical server
// logs — namely, nothing. `focus-diag.ts` records the client side beautifully
// but lives in `window.__loomFocusDiag`, which requires a live browser and the
// user still sitting on the broken tab. By the time anyone looks, it is gone.
//
// So every dispatch attempt for the frames that carry real user intent is
// reported to the server, where it lands in Cloud Logging under the session's
// trace and outlives the tab.
//
// CONTENT-FREE. Only the frame KIND (a fixed protocol enum), booleans
// and counts are sent. No message text, no field values, no ids beyond the
// session's own. The payload shape is closed — see `ClientTraceEvent`.
//
// Cheap by construction: batched on a short debounce, hard-capped per session,
// fire-and-forget, and flushed via `sendBeacon` on pagehide so the last batch
// before a tab close still lands.

/** Events worth the round trip. Deliberately small — this is not analytics. */
export type ClientTraceEvent =
  /** A frame was handed to the socket. `delivered` = the socket was OPEN. */
  | "frame_send"
  /** The server's ingress ack came back and released the outbox entry. */
  | "frame_ack"
  /** A frame was redelivered from the outbox after a reconnect. */
  | "frame_retry"
  /** The outbox dropped an entry without ever confirming delivery. */
  | "frame_dropped"
  /** The human-idle watchdog `fired` (session DELETEd) or was `dismissed`. */
  | "idle_kill"
  /** A resume of an idle-killed session: `send`, `accepted` or `refused`. */
  | "idle_wake"
  /**
   * Which session a surface was handed by `useProjectSession.ensure()`, and
   * from where: the tab's in-memory cache, the per-tab sessionStorage mirror,
   * a joined in-flight mint, or a fresh `POST /sessions`. Posted straight to
   * THAT session's trace (`reportClientEventTo`) — the reporter may be pointed
   * at another session, or none, at that moment.
   */
  | "session.bind"
  /** RefineFlow sent its flow-opening seed. */
  | "refine.seed_send"
  /** A control on a Focus stopped / done / parked screen was pressed. */
  | "ui.click"
  /** The "you are still in the middle of #N" bar rendered for a parked issue. */
  | "refine.parked_notice"
  /** …and was still on screen, unbroken, `heldMs` later — not a replay flash. */
  | "refine.parked_notice_shown"
  /** The refine screen changed: which step, and which controls it offered. */
  | "focus.stage"
  /** The refine wait line changed kind — what the page told the user while it
   *  waited, and the server facts it read. */
  | "focus.wait";

/**
 * The frame-level events above are budgeted separately from the screen-level
 * ones below them, so a long session's stage changes can never spend the
 * budget the frame trace needs (and the reverse).
 */
const UI_EVENTS: ReadonlySet<ClientTraceEvent> = new Set<ClientTraceEvent>([
  "session.bind",
  "refine.seed_send",
  "ui.click",
  "refine.parked_notice",
  "refine.parked_notice_shown",
  "focus.stage",
  "focus.wait",
]);

export interface ClientTraceFields {
  /** Protocol frame kind (`user_message`, `readiness_accept`, …). */
  readonly kind?: string;
  /** Whether the write reached an OPEN socket at attempt time. */
  readonly delivered?: boolean;
  /** Outbox depth after the attempt — a rising number means stuck frames. */
  readonly outboxLen?: number;
  /** Milliseconds from send attempt to ack. */
  readonly ackMs?: number;
  /** Why an entry was dropped (`expired`, `scope`, `session_change`). */
  readonly why?: string;
  /**
   * Milliseconds the user waited on something. On `idle_wake` this is the whole
   * `POST /reconnect` round trip — the number that says whether the composer
   * held for two seconds or for twenty.
   */
  readonly waitMs?: number;
  // Screen-level diagnostics. Enums, ids, counts and booleans — never text.
  /** `session.bind`: `memory` | `sessionStorage` | `adopted` | `claimed` |
   *  `legacy` | `inflight` | `minted` (see `useProjectSession`'s `traceBind`). */
  readonly source?: string;
  /** `session.bind`: the binding slot (`issue` | `chat`). */
  readonly slot?: string;
  readonly projectId?: number;
  /** The issue the surface is on. */
  readonly issueIid?: number;
  /** `refine.seed_send`: the mount was a resume. */
  readonly resumed?: boolean;
  /** `refine.seed_send`: ms from mount to the seed send. */
  readonly waitedMs?: number;
  /** `refine.seed_send`: issue of the newest `user_message` already on the
   *  timeline when the seed went out; null when none carried one. */
  readonly priorFlowIssueIid?: number | null;
  /** The sandbox phase the client held at that moment. */
  readonly sandboxPhase?: string;
  /** `ui.click`: which control. */
  readonly control?: string;
  /** `ui.click` / accept click: which screen the control was on. */
  readonly surface?: string;
  /** `refine.parked_notice`: the issue the bar names. */
  readonly parkedIid?: number;
  /** `refine.parked_notice`: the client's single apply-result slot. */
  readonly applyResultIssueIid?: number | null;
  readonly applyResultOk?: boolean | null;
  /** `refine.parked_notice*`: timeline entries the client held at that moment. */
  readonly timelineLen?: number;
  /** `refine.parked_notice_shown`: how long the bar stayed on screen unbroken. */
  readonly heldMs?: number;
  /** `focus.stage`: the refine step on screen. */
  readonly stage?: string;
  readonly pendingEditCount?: number;
  /** `focus.stage`: "keep the previous value" is on screen. */
  readonly cancelVisible?: boolean;
  /** `focus.stage`: an Accept / Apply control is on screen. */
  readonly acceptVisible?: boolean;
  /** `focus.wait`: `offline` | `unreachable` | `starting` | `reconnecting` |
   *  `working` | `stopped` | `noWorkspace` | `none` (no wait drawn). */
  readonly waitKind?: string;
  /** `focus.wait`: the activity bucket named (`code`, `card`, …) — never a
   *  tool's input. */
  readonly activity?: string;
  /** `focus.wait`: why the last turn stopped (`silence`). */
  readonly reason?: string;
  /** `focus.wait`: the server says the agent is busy. */
  readonly busy?: boolean;
  /** `focus.wait`: "taking longer than usual" / "hasn't started yet" is shown. */
  readonly longShown?: boolean;
  /** `focus.wait`: seconds into the busy stretch or the phase. */
  readonly elapsedS?: number;
  /** `focus.wait`: the kind of wait this line ENDS… */
  readonly endedKind?: string;
  /** …and how long it lasted, in seconds. */
  readonly endedElapsedS?: number;
}

interface Batched extends ClientTraceFields {
  readonly event: ClientTraceEvent;
  /** Milliseconds since page load — enough to order a batch without a clock. */
  readonly at: number;
}

/**
 * Per-session cap. A pathological reconnect loop must not turn a debugging aid
 * into a log-volume incident, so the reporter goes permanently quiet once a
 * session has spent its budget. ~20 events is a healthy refine; 200 leaves
 * generous room for a genuinely broken one.
 */
const MAX_EVENTS_PER_SESSION = 200;
const FLUSH_DEBOUNCE_MS = 2_000;
const MAX_BATCH = 40;

let queue: Batched[] = [];
let sent = 0;
let sentUi = 0;
/** Page-wide cap for `reportClientEventTo` (one POST each). */
const MAX_DIRECT_EVENTS = 100;
let sentDirect = 0;
let currentSessionId: string | null = null;
let timer: ReturnType<typeof setTimeout> | null = null;
let listenerBound = false;

// `/sessions/*` is proxied straight through to apps/server (vite.config.ts) —
// same origin, so the signed `loom_session` cookie rides along.
const endpoint = (sessionId: string): string =>
  `/sessions/${encodeURIComponent(sessionId)}/client-trace`;

const flush = (useBeacon = false): void => {
  if (timer !== null) {
    clearTimeout(timer);
    timer = null;
  }
  const sessionId = currentSessionId;
  if (sessionId === null || queue.length === 0) return;
  const batch = queue.slice(0, MAX_BATCH);
  queue = queue.slice(batch.length);
  const body = JSON.stringify({ events: batch });
  try {
    // `sendBeacon` is the only transport that survives a pagehide; ordinary
    // fetch is cancelled with the document. It has no credentials option
    // because it always sends them for a same-origin URL, which this is.
    if (useBeacon && typeof navigator !== "undefined" && navigator.sendBeacon) {
      navigator.sendBeacon(
        endpoint(sessionId),
        new Blob([body], { type: "application/json" }),
      );
      return;
    }
    void fetch(endpoint(sessionId), {
      method: "POST",
      credentials: "same-origin",
      headers: { "content-type": "application/json" },
      body,
      keepalive: true,
    }).catch(() => {
      /* diagnostics must never surface an error to the user */
    });
  } catch {
    /* diagnostics must never throw */
  }
};

const bindUnloadFlush = (): void => {
  if (listenerBound || typeof window === "undefined") return;
  listenerBound = true;
  window.addEventListener("pagehide", () => flush(true));
};

/**
 * Point the reporter at a session. Resets the per-session budget, because the
 * cap exists to bound ONE session's noise, not a user's whole browsing day.
 * Any queued events for the previous session are flushed first so a session
 * switch cannot swallow the evidence of what just went wrong.
 */
export const setClientTraceSession = (sessionId: string | null): void => {
  if (sessionId === currentSessionId) return;
  flush();
  currentSessionId = sessionId;
  queue = [];
  sent = 0;
  sentUi = 0;
};

/** Record one attempt. Fire-and-forget; never throws, never blocks a send. */
export const reportClientEvent = (
  event: ClientTraceEvent,
  fields: ClientTraceFields = {},
): void => {
  if (currentSessionId === null) return;
  if (UI_EVENTS.has(event)) {
    if (sentUi >= MAX_EVENTS_PER_SESSION) return;
    sentUi += 1;
  } else {
    if (sent >= MAX_EVENTS_PER_SESSION) return;
    sent += 1;
  }
  queue.push({
    event,
    at: Math.round(
      typeof performance !== "undefined" ? performance.now() : 0,
    ),
    ...fields,
  });
  bindUnloadFlush();
  if (queue.length >= MAX_BATCH) {
    flush();
    return;
  }
  if (timer === null) {
    timer = setTimeout(() => flush(), FLUSH_DEBOUNCE_MS);
  }
};

/**
 * Record one event onto a NAMED session's trace, now, in a POST of its own.
 *
 * For the one fact that has to land on a session the batching reporter is not
 * pointed at: which session `ensure()` just handed a surface, reported before
 * that surface has opened a socket to it. Page-wide cap, fire-and-forget,
 * never throws.
 */
export const reportClientEventTo = (
  sessionId: string,
  event: ClientTraceEvent,
  fields: ClientTraceFields = {},
): void => {
  if (sentDirect >= MAX_DIRECT_EVENTS) return;
  sentDirect += 1;
  try {
    const body = JSON.stringify({
      events: [
        {
          event,
          at: Math.round(
            typeof performance !== "undefined" ? performance.now() : 0,
          ),
          ...fields,
        },
      ],
    });
    void fetch(endpoint(sessionId), {
      method: "POST",
      credentials: "same-origin",
      headers: { "content-type": "application/json" },
      body,
      keepalive: true,
    }).catch(() => {
      /* diagnostics must never surface an error to the user */
    });
  } catch {
    /* diagnostics must never throw */
  }
};

/** Test seam — drains state so cases cannot bleed into each other. */
export const __resetClientTrace = (): void => {
  if (timer !== null) clearTimeout(timer);
  timer = null;
  queue = [];
  sent = 0;
  sentUi = 0;
  sentDirect = 0;
  currentSessionId = null;
};
